import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, CircularProgress, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useAdminGpsHistory } from '@/hooks/useGpsHistory';
import GpsRouteMap from '../maps/GpsRouteMap';

interface UserGpsRouteModalProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  userName?: string;
}

interface GpsPoint {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
}

const UserGpsRouteModal: React.FC<UserGpsRouteModalProps> = ({
  open,
  onClose,
  userId,
  userName,
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  // Calculate start and end of selected day for the query
  const dateRange = useMemo(() => {
    if (!selectedDate) return { startDate: '', endDate: '' };
    return {
      startDate: selectedDate.startOf('day').toISOString(),
      endDate: selectedDate.endOf('day').toISOString(),
    };
  }, [selectedDate]);

  // Use React Query hook to fetch GPS data (only when modal is open)
  const { data, isLoading, error: queryError } = useAdminGpsHistory(
    open ? userId : undefined,
    dateRange.startDate,
    dateRange.endDate,
    0,
    1000
  );

  // Process GPS points from the query response
  const gpsPoints = useMemo(() => {
    if (!data?.list || data.list.length === 0) return [];
    
    const points: GpsPoint[] = data.list.map((track: any) => ({
      latitude: parseFloat(track.latitude),
      longitude: parseFloat(track.longitude),
      timestamp: track.timestamp,
      accuracy: track.accuracy ? parseFloat(track.accuracy) : undefined,
    }));
    
    // Sort by timestamp to ensure chronological order
    points.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    return points;
  }, [data]);

  const loading = isLoading;
  const error = queryError ? (queryError as Error).message : null;

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: '8px' },
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            GPS Route {userName && `- ${userName}`}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={onClose}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : gpsPoints.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography variant="body1" color="text.secondary">
              No GPS data available for the selected date
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {gpsPoints.length} GPS point{gpsPoints.length !== 1 ? 's' : ''} found
            </Typography>
            <GpsRouteMap gpsPoints={gpsPoints} height="500px" />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserGpsRouteModal;
